<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const statusScan = [
  {
    status: 'Sedang Diproses',
    color: 'info',
    icon: 'ri-loader-4-line',
    desc: 'Foto sedang dibaca oleh AI (OCR). Biasanya selesai dalam beberapa detik.',
    aksi: 'Tunggu, halaman otomatis diperbarui',
  },
  {
    status: 'Berhasil Dibaca',
    color: 'success',
    icon: 'ri-file-text-line',
    desc: 'AI selesai membaca struk. Hasil ekstraksi item siap direview dan dikoreksi.',
    aksi: 'Cek & koreksi tiap baris item',
  },
  {
    status: 'Gagal Dibaca',
    color: 'error',
    icon: 'ri-error-warning-line',
    desc: 'Foto terlalu buram/gelap sehingga AI tidak bisa membaca isinya.',
    aksi: 'Scan ulang atau input manual',
  },
  {
    status: 'Sudah Dikonversi',
    color: 'secondary',
    icon: 'ri-checkbox-circle-line',
    desc: 'Hasil scan sudah dijadikan Purchase Receiving. Stok sudah bertambah.',
    aksi: 'Lihat detail Purchase Receiving',
  },
]

const itemStatus = [
  {
    status: 'Otomatis',
    color: 'success',
    icon: 'ri-magic-line',
    desc: 'AI berhasil mencocokkan nama item di struk dengan Bahan Baku yang sudah terdaftar di sistem Anda. Biasanya tidak perlu diubah.',
    aksi: 'Cek sekilas saja, biasanya sudah benar',
  },
  {
    status: 'Perlu Dicek',
    color: 'warning',
    icon: 'ri-search-eye-line',
    desc: 'AI menemukan kemiripan nama, tapi tidak 100% yakin (misal "Kopi Arabika" vs "Kopi Arabica Gayo"). Perlu Anda konfirmasi.',
    aksi: 'Cek dropdown Bahan Baku, ganti jika perlu',
  },
  {
    status: 'Tidak Dikenali',
    color: 'error',
    icon: 'ri-question-line',
    desc: 'AI tidak menemukan Bahan Baku yang cocok di sistem — mungkin belum terdaftar, atau tulisan di struk terlalu berbeda.',
    aksi: 'WAJIB pilih manual sebelum lanjut',
  },
  {
    status: 'Manual',
    color: 'info',
    icon: 'ri-hand-coin-line',
    desc: 'Anda sudah memilih atau mengubah Bahan Baku secara manual untuk baris ini.',
    aksi: 'Sudah beres, tidak perlu aksi lagi',
  },
]

const faq = [
  {
    q: 'Format foto apa saja yang didukung?',
    a: 'JPG, PNG, atau WEBP, maksimal ukuran file 5MB. Anda bisa langsung ambil foto pakai kamera HP (tombol akan membuka kamera belakang otomatis) atau pilih foto dari galeri.',
  },
  {
    q: 'Kenapa AI salah baca / hasilnya berantakan?',
    a: 'Penyebab paling umum: foto buram, pencahayaan kurang, struk kusut/terlipat, atau tulisan struk sudah pudar (kertas thermal lama). Coba foto ulang dengan pencahayaan cukup, kamera tegak lurus di atas struk, dan pastikan seluruh struk masuk frame tanpa terpotong.',
  },
  {
    q: 'Kenapa semua item saya berstatus "Tidak Dikenali"?',
    a: 'Ini terjadi jika Bahan Baku di struk belum pernah didaftarkan di menu Inventory → Bahan Baku. AI hanya bisa mencocokkan ke data yang sudah ada di sistem Anda, bukan menebak-nebak atau membuat data baru otomatis. Daftarkan dulu bahan bakunya, atau pilih manual dari dropdown yang tersedia.',
  },
  {
    q: 'Apakah saya wajib mengoreksi semua baris sebelum lanjut?',
    a: 'Tidak wajib untuk baris "Otomatis" dan "Perlu Dicek" (walau disarankan tetap dicek sekilas). Tapi baris berstatus "Tidak Dikenali" WAJIB dipilih Bahan Bakunya secara manual — tombol "Buat Purchase Receiving" akan tetap nonaktif (disabled) selama masih ada baris Tidak Dikenali.',
  },
  {
    q: 'Bisa ubah Qty, Satuan, atau Harga hasil bacaan AI?',
    a: 'Bisa. Semua kolom (Bahan Baku, Qty, Satuan, Harga Satuan) bisa langsung diedit di tabel. Perubahan otomatis tersimpan begitu Anda pindah dari kolom tersebut (blur) atau ganti pilihan dropdown — tidak ada tombol simpan terpisah.',
  },
  {
    q: 'Setelah "Buat Purchase Receiving", apakah stok langsung bertambah?',
    a: 'Ya. Setelah dikonversi, hasil scan otomatis menjadi Purchase Receiving lewat proses yang sama persis dengan input manual — jadi validasi harga, kalkulasi HPP, dan penambahan stok mengikuti alur baku yang sudah Anda pakai selama ini. Tidak ada logic terpisah khusus AI untuk urusan stok/HPP.',
  },
  {
    q: 'Apakah Purchase Receiving hasil scan bisa diedit lagi setelahnya?',
    a: 'Setelah dikonversi, halaman scan berubah jadi read-only (semua kolom terkunci) dan menampilkan link ke Purchase Receiving yang terbentuk. Untuk perubahan lebih lanjut, edit langsung dari halaman Purchase Receiving tersebut, bukan dari halaman scan.',
  },
  {
    q: 'Kalau AI gagal total membaca struk, apa yang harus saya lakukan?',
    a: 'Halaman akan menampilkan pesan "Gagal membaca struk" beserta opsi untuk mencoba scan ulang, atau langsung gunakan form Purchase Receiving manual seperti biasa — fitur manual tetap tersedia penuh sebagai cadangan.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <div class="d-flex align-center gap-2 mb-3">
      <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
        User Guide
      </RouterLink>
      <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
      <RouterLink to="/userguide/pembelian" class="text-decoration-none text-medium-emphasis text-body-2">
        Pembelian
      </RouterLink>
      <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
      <span class="text-body-2 font-weight-medium">Scan Struk (AI)</span>
    </div>

    <!-- Header -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="primary" variant="tonal" size="64" rounded="lg" class="me-4">
            <VIcon icon="ri-scan-2-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">Scan Struk Belanja (AI)</h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Foto struk dari supplier, biarkan AI yang mengetik item, qty, dan harganya
            </p>
          </div>
        </div>
        <VAlert type="info" variant="tonal">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda menggunakan fitur Scan Struk (AI) — cara cepat mencatat penerimaan barang
          dari struk fisik supplier tanpa mengetik satu-satu secara manual.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="primary" />
            Apa itu Scan Struk (AI)?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Scan Struk (AI)</strong> adalah cara cepat membuat Purchase Receiving
              (penerimaan barang) dengan cukup memfoto struk belanja fisik dari supplier —
              sistem akan otomatis membaca nama item, jumlah, satuan, dan harga dari foto tersebut.
            </p>

            <VAlert type="success" variant="tonal" class="mb-3">
              <strong>Contoh nyata:</strong>
              Anda baru belanja bahan baku ke pasar/supplier dan dapat struk kertas berisi
              15 item berbeda. Daripada mengetik satu-satu ke form manual (bisa 10–15 menit
              dan rawan salah ketik), cukup foto struknya — AI membaca semua item dalam
              hitungan detik, Anda tinggal cek ulang lalu konfirmasi.
            </VAlert>

            <p class="text-body-2 mb-0">
              Fitur ini <strong>bukan pengganti</strong> Purchase Receiving manual — melainkan
              cara input alternatif yang lebih cepat. Hasil bacaan AI tetap harus Anda review
              dan koreksi bila perlu sebelum benar-benar mengubah stok.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Status Scan -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-git-branch-line" class="me-2" color="info" />
            Status Proses Scan — Apa Artinya?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Setelah foto diupload, prosesnya melewati beberapa status berikut:
            </p>
            <VRow>
              <VCol
                v-for="s in statusScan"
                :key="s.status"
                cols="12"
                sm="6"
                lg="true"
              >
                <VCard variant="tonal" :color="s.color" class="h-100">
                  <VCardText class="pa-3">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VIcon :icon="s.icon" size="20" />
                      <VChip :color="s.color" size="small" label>
                        {{ s.status }}
                      </VChip>
                    </div>
                    <p class="text-body-2 mb-2">{{ s.desc }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      <strong>Aksi:</strong> {{ s.aksi }}
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Pakai -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-camera-line" class="me-2" color="success" />
            Cara Scan Struk
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-0">
              <li class="mb-4">
                Buka menu <strong>Pembelian → Supplier &amp; PO → Scan Struk (AI)</strong>
                dari sidebar kiri
              </li>

              <li class="mb-4">
                <p class="mb-2">
                  (Opsional) Pilih <strong>Supplier</strong> dari dropdown, kalau Anda sudah
                  tahu struk ini dari supplier mana. Boleh dikosongkan dan diisi belakangan.
                </p>
              </li>

              <li class="mb-4">
                <p class="mb-2">
                  Klik kotak <strong>"Ambil Foto Struk / Pilih dari Galeri"</strong>:
                </p>
                <VList density="compact" class="mb-2">
                  <VListItem prepend-icon="ri-smartphone-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      Di HP, ini langsung membuka <strong>kamera belakang</strong> — foto struknya langsung
                    </VListItemTitle>
                  </VListItem>
                  <VListItem prepend-icon="ri-computer-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      Di komputer, ini membuka jendela pilih file dari galeri/folder
                    </VListItemTitle>
                  </VListItem>
                </VList>
                <VAlert type="info" variant="tonal" density="compact" class="text-body-2">
                  Format yang didukung: <strong>JPG, PNG, WEBP</strong>, maksimal ukuran <strong>5MB</strong>.
                </VAlert>
              </li>

              <li class="mb-4">
                Cek preview foto yang muncul. Kalau kurang jelas, klik
                <strong>"Ganti Foto"</strong> dan foto ulang.
              </li>

              <li class="mb-4">
                Klik <strong>"Upload &amp; Proses"</strong>. Anda akan diarahkan ke halaman
                review, dan AI mulai membaca struk (status <strong>"Sedang Diproses"</strong>).
              </li>

              <li>
                Tunggu beberapa detik sampai statusnya berubah jadi hasil ekstraksi —
                lanjut ke bagian <strong>"Review &amp; Koreksi Hasil"</strong> di bawah.
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Review & Koreksi -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-edit-2-line" class="me-2" color="warning" />
            Review &amp; Koreksi Hasil Ekstraksi
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Setelah AI selesai membaca, Anda akan melihat tabel berisi item-item yang
              berhasil dibaca dari struk. Di bawah tiap nama Bahan Baku, ada teks kecil
              <code>OCR: "..."</code> yang menunjukkan baris asli hasil pembacaan struk —
              berguna untuk membandingkan kalau ada yang meragukan.
            </p>

            <p class="text-body-2 mb-2">Kolom yang bisa Anda cek/edit langsung di tabel:</p>
            <VTable class="mb-4">
              <thead>
                <tr>
                  <th style="min-width: 140px">Kolom</th>
                  <th>Penjelasan</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Bahan Baku</strong></td>
                  <td class="text-body-2 py-2">
                    Dropdown pencarian Bahan Baku. Kalau AI salah cocokkan, ketik nama yang
                    benar dan pilih dari daftar. Wajib diisi untuk semua baris.
                  </td>
                </tr>
                <tr>
                  <td><strong>Qty</strong></td>
                  <td class="text-body-2 py-2">Jumlah barang, bisa diedit langsung (angka desimal diperbolehkan).</td>
                </tr>
                <tr>
                  <td><strong>Satuan (teks)</strong></td>
                  <td class="text-body-2 py-2">Satuan sesuai yang tertulis di struk, mis. "kg", "liter", "pcs".</td>
                </tr>
                <tr>
                  <td><strong>Harga Satuan</strong></td>
                  <td class="text-body-2 py-2">Harga beli per satuan dalam Rupiah.</td>
                </tr>
              </tbody>
            </VTable>

            <VAlert type="success" variant="tonal" density="compact" class="mb-4 text-body-2">
              <strong>Tidak ada tombol "Simpan" terpisah.</strong>
              Setiap perubahan otomatis tersimpan begitu Anda pindah dari kolom (klik di luar)
              atau mengganti pilihan Bahan Baku.
            </VAlert>

            <p class="text-body-2 mb-3">
              Kolom terakhir, <strong>Status</strong>, menunjukkan seberapa yakin AI dengan
              hasil pencocokan Bahan Baku untuk baris tersebut:
            </p>

            <VRow>
              <VCol
                v-for="s in itemStatus"
                :key="s.status"
                cols="12"
                sm="6"
                lg="3"
              >
                <VCard variant="tonal" :color="s.color" class="h-100">
                  <VCardText class="pa-3">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VIcon :icon="s.icon" size="20" />
                      <VChip :color="s.color" size="small" label>{{ s.status }}</VChip>
                    </div>
                    <p class="text-body-2 mb-2">{{ s.desc }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      <strong>{{ s.aksi }}</strong>
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Konversi ke Receiving -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-checkbox-circle-line" class="me-2" color="success" />
            Konversi ke Purchase Receiving
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Setelah semua baris item sudah benar, klik tombol
              <strong>"Buat Purchase Receiving"</strong> di pojok kanan bawah tabel.
            </p>
            <VAlert type="warning" variant="tonal" class="mb-3 text-body-2">
              <strong>⚠️ Tombol ini nonaktif (disabled) jika:</strong>
              <ul class="ps-4 mt-1 mb-0">
                <li>Masih ada baris berstatus <strong>"Tidak Dikenali"</strong> — lengkapi dulu Bahan Bakunya</li>
                <li>Scan ini sudah pernah dikonversi sebelumnya</li>
                <li>Tidak ada item sama sekali untuk dikonversi</li>
              </ul>
            </VAlert>
            <p class="text-body-2 mb-0">
              Setelah diklik, sistem otomatis membuat <strong>Purchase Receiving</strong> baru
              berisi semua item tersebut, dan Anda akan diarahkan ke halaman detail Receiving-nya.
              Stok bahan baku bertambah mengikuti alur Purchase Receiving normal — sama seperti
              kalau Anda input manual.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard color="primary" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Supaya AI Membaca Struk Lebih Akurat
          </VCardTitle>
          <VCardText>
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Foto tegak lurus di atas struk.</strong>
                  Hindari sudut miring — foto dari atas persis akan lebih mudah dibaca daripada foto menyamping.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Pastikan pencahayaan cukup.</strong>
                  Foto di tempat terang, hindari bayangan tangan/kepala menutupi tulisan struk.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Ratakan struk sebelum difoto.</strong>
                  Struk yang kusut atau terlipat membuat sebagian tulisan sulit terbaca.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Daftarkan Bahan Baku terlebih dulu.</strong>
                  Makin lengkap daftar Bahan Baku Anda, makin banyak baris yang otomatis
                  berstatus "Otomatis" — mengurangi kerja koreksi manual.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Tetap cek sebelum konfirmasi.</strong>
                  AI membantu mempercepat, tapi harga dan qty tetap sebaiknya dicek sekilas —
                  struk yang benar-benar buram bisa membuat AI salah baca angka.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mb-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Next Steps -->
    <VRow>
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Langkah Selanjutnya</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Bahan Baku', sub: 'Daftarkan bahan baku dulu supaya AI bisa mencocokkan', icon: 'ri-archive-line', color: 'info', to: '/userguide/produk-inventory/bahan-baku' },
              { title: 'Purchase Order', sub: 'Alternatif buat pesanan resmi sebelum barang datang', icon: 'ri-file-list-3-line', color: 'warning', to: '/userguide/pembelian/purchase-order' },
              { title: 'Receiving Barang', sub: 'Cara input penerimaan barang secara manual', icon: 'ri-truck-line', color: 'success', to: '/userguide/pembelian/receiving' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
